import React from 'react';
import ReactDOM from 'react-dom/client';
import Keycloak from 'keycloak-js';
import { AuthContext } from 'alurkerja-ui';
import { axiosInstance } from './utils/libs/axios';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { env } from './utils/configs/env';
import {
  createBrowserRouter,
  RouterProvider,
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
} from 'react-router-dom';
import { routes } from './routes/root';
import * as Sentry from '@sentry/react';
import { ReactKeycloakProvider } from '@react-keycloak/web';
import { AccessProvider } from './utils/hooks/useAccess';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

import './assets/index.css';
import 'alurkerja-ui/dist/style.css';
import { ToastContainer } from 'react-toastify';
import { MenuProvider } from './utils/hooks/useMenu';

// react-query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

// keycloak
const keycloakInstance = new Keycloak({
  url: env.keycloakUrl,
  realm: env.keycloakRealm,
  clientId: env.keycloakClientId,
});

// router
const router = createBrowserRouter(routes);

// sentry
Sentry.init({
  dsn: env.sentryDsn,
  environment: env.environment,
  integrations: [
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect: React.useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
    Sentry.replayIntegration(),
  ],
  tracesSampleRate: 1.0,
  // Capture Replay for 10% of all sessions,
  // plus for 100% of sessions with an error
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
});

ReactDOM.createRoot(document.getElementById('root')!).render(
  <ReactKeycloakProvider authClient={keycloakInstance}>
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <AuthContext.Provider value={axiosInstance}>
          <AccessProvider>
            <MenuProvider>
              <RouterProvider router={router} />

              <ToastContainer />
            </MenuProvider>
          </AccessProvider>
        </AuthContext.Provider>

        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </React.StrictMode>
  </ReactKeycloakProvider>,
);
